import { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import useSecurity from "../hooks/useSecurity";
import useData from "../hooks/useData";

const normalizePath = (value) => {
  if (!value || typeof value !== "string") {
    return "/";
  }

  if (value === "/") {
    return value;
  }

  const trimmed = value.endsWith("/") ? value.slice(0, -1) : value;
  return trimmed || "/";
};

const ProtectedRoutes = () => {
  const { isAuthenticated } = useSecurity();
  const { executeAction } = useData();
  const location = useLocation();
  const role = useSelector((state) => state.authentication.role);
  const userId = useSelector((state) => state.authentication.userId);
  const pageSecurityRole = role === "teamLead" ? "supervisor" : role;
  const [isLoaded, setIsLoaded] = useState(false);
  const [allowedPaths, setAllowedPaths] = useState([]);
  const [securedPaths, setSecuredPaths] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadPageSecurity = async () => {
      if (!isAuthenticated || !role) {
        if (isMounted) {
          setAllowedPaths([]);
          setSecuredPaths([]);
          setIsLoaded(true);
        }
        return;
      }

      setIsLoaded(false);

      const [menuResults, allPagesResults] = await Promise.all([
        executeAction({
          entityName: "PageSecurity",
          action: "getMenu",
          data: { role: pageSecurityRole, userId },
        }),
        executeAction({
          entityName: "Page",
          action: "get",
          filter: { isActive: true, showInMenu: true },
        }),
      ]);

      if (!isMounted) {
        return;
      }

      const nextAllowedPaths =
        menuResults?.isSuccessful && Array.isArray(menuResults?.data)
          ? menuResults.data
              .map((page) => normalizePath(page?.routePath))
              .filter(Boolean)
          : [];

      const nextSecuredPaths =
        allPagesResults?.isSuccessful && Array.isArray(allPagesResults?.data)
          ? allPagesResults.data
              .map((page) => normalizePath(page?.routePath))
              .filter(Boolean)
          : [];

      setAllowedPaths(Array.from(new Set(nextAllowedPaths)));
      setSecuredPaths(Array.from(new Set(nextSecuredPaths)));
      setIsLoaded(true);
    };

    loadPageSecurity();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, role, pageSecurityRole, userId]);

  const effectiveAllowedPaths = useMemo(() => {
    if (role === "client") {
      return ["/client-portal"];
    }

    const paths = new Set(allowedPaths);

    // For reps, /home is an alias route that immediately redirects to /rep-home.
    // Treat them as equivalent permissions to avoid redirect ping-pong.
    if (role === "rep" && paths.has("/home")) {
      paths.add("/rep-home");
    }

    if (role === "rep" && paths.has("/rep-home")) {
      paths.add("/home");
    }

    return Array.from(paths);
  }, [allowedPaths, role]);

  const resolvedAllowedPaths = useMemo(() => {
    return effectiveAllowedPaths;
  }, [effectiveAllowedPaths]);

  const redirectPath = useMemo(() => {
    if (role === "client") {
      return "/client-portal";
    }

    if (role === "rep" && resolvedAllowedPaths.includes("/rep-home")) {
      return "/rep-home";
    }

    if (resolvedAllowedPaths.includes("/home")) {
      return "/home";
    }

    if (resolvedAllowedPaths.length > 0) {
      return resolvedAllowedPaths[0];
    }

    return role === "rep" ? "/rep-home" : "/home";
  }, [resolvedAllowedPaths, role]);

  const pathname = normalizePath(location.pathname);
  const requiresPermission = securedPaths.includes(pathname);
  const isAllowedPath = resolvedAllowedPaths.includes(pathname);
  const hasSecurityContext = resolvedAllowedPaths.length > 0;
  const canRedirect =
    Boolean(redirectPath) &&
    redirectPath !== pathname &&
    resolvedAllowedPaths.includes(redirectPath);

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isLoaded) {
    return null;
  }

  // Client users should always land on the client portal route.
  // This prevents browser session-restore from reopening stale internal dashboards.
  if (role === "client" && pathname !== "/client-portal") {
    return (
      <Navigate
        to="/client-portal"
        replace
      />
    );
  }

  if (
    hasSecurityContext &&
    requiresPermission &&
    !isAllowedPath &&
    canRedirect
  ) {
    return (
      <Navigate
        to={redirectPath}
        replace
      />
    );
  }

  return <Outlet />;
};

export default ProtectedRoutes;
